import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IonPopover } from '@ionic/angular';

@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true
    }
  ]
})
export class MultiSelectComponent implements OnInit, OnChanges, ControlValueAccessor {
  @ViewChild('popover') popover!: IonPopover;

  // Input Properties
  @Input() options: any[] = [];
  @Input() displayField: string = 'label';
  @Input() valueField: string = 'value';
  @Input() placeholder: string = 'Select items';
  @Input() title: string = '';
  @Input() subtitle: string = '';
  @Input() icon: string = '';
  @Input() searchable: boolean = true;
  @Input() showSelectAll: boolean = true;
  @Input() disabled: boolean = false;
  @Input() maxSelections: number = 0; // 0 = unlimited
  // Quando true il filtraggio NON avviene client-side: ogni cambio del searchTerm viene
  // emesso via (searchTermChange) e il consumer è responsabile di aggiornare [options].
  @Input() serverSideSearch: boolean = false;
  // Mostra un loader interno (search-bar / list area) quando il consumer sta facendo fetch
  // delle options (utile in modalità serverSideSearch).
  @Input() isLoading: boolean = false;

  // Output Events
  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() confirmSelection = new EventEmitter<any[]>();
  @Output() cancelSelection = new EventEmitter<void>();
  @Output() searchTermChange = new EventEmitter<string>();

  // Internal State
  selectedItems: any[] = [];
  tempSelectedItems: any[] = [];
  searchTerm: string = '';
  filteredOptions: any[] = [];
  isPopoverOpen: boolean = false;

  // ControlValueAccessor
  private onChange = (value: any) => {};
  private onTouched = () => {};

  ngOnInit() {
    this.filteredOptions = [...this.options];
  }

  ngOnChanges(changes: SimpleChanges) {
    // Filtra solo se cambia davvero il contenuto della lista. Per inputs
    // collaterali (isLoading, disabled, placeholder, ecc.) lo spread di
    // [...options] e' inutile e contribuisce ai freeze di CD su pagine
    // con piu' multi-select serverSide.
    if (changes['options']) {
      this.filterOptions();
    }
  }

  // ControlValueAccessor Implementation
  writeValue(value: any[]): void {
    this.selectedItems = value || [];
    this.tempSelectedItems = [...this.selectedItems];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Popover Management
  openPopover(event: Event) {
    if (this.disabled) return;

    this.tempSelectedItems = [...this.selectedItems];
    // Memorizza se c'era un searchTerm precedente prima del reset, per decidere
    // se serve davvero richiedere al consumer una ricarica "pulita".
    const hadPreviousSearch = !!this.searchTerm;
    this.searchTerm = '';
    this.isPopoverOpen = true;
    this.filterOptions();

    // Reset del search term ad ogni riapertura in modalità serverSideSearch:
    // emettiamo SOLO se serve davvero ricaricare (cioè se prima c'era una ricerca
    // attiva, oppure se non abbiamo ancora opzioni in cache). Altrimenti riusiamo
    // le options gia' caricate ed evitiamo un round-trip backend + CD storm.
    if (this.serverSideSearch && (hadPreviousSearch || this.options.length === 0)) {
      this.searchTermChange.emit('');
    }
  }

  closePopover() {
    this.isPopoverOpen = false;
    this.searchTerm = '';
  }

  // Selection Logic
  toggleOption(option: any) {
    const value = this.getOptionValue(option);
    const index = this.tempSelectedItems.findIndex(item => 
      this.getOptionValue(item) === value
    );

    if (index > -1) {
      // Deseleziona l'opzione cliccata
      this.tempSelectedItems.splice(index, 1);
    } else {
      // 🆕 Modalità single-select: se maxSelections === 1, sostituisci invece di aggiungere
      if (this.maxSelections === 1) {
        // Sostituisci completamente la selezione con la nuova opzione
        this.tempSelectedItems = [option];
      } else if (this.maxSelections === 0 || this.tempSelectedItems.length < this.maxSelections) {
        // Modalità multi-select: aggiungi se non hai raggiunto il limite
        this.tempSelectedItems.push(option);
      }
    }
  }

  isSelected(option: any): boolean {
    const value = this.getOptionValue(option);
    return this.tempSelectedItems.some(item => 
      this.getOptionValue(item) === value
    );
  }

  toggleSelectAll() {
    if (this.isAllSelected()) {
      this.tempSelectedItems = [];
    } else {
      this.tempSelectedItems = [...this.filteredOptions];
    }
  }

  isAllSelected(): boolean {
    return this.filteredOptions.length > 0 && 
           this.filteredOptions.every(option => this.isSelected(option));
  }

  isSomeSelected(): boolean {
    return this.tempSelectedItems.length > 0 && !this.isAllSelected();
  }

  clearAll() {
    this.tempSelectedItems = [];
  }

  // Actions
  applySelection() {
    this.selectedItems = [...this.tempSelectedItems];
    this.onChange(this.selectedItems);
    this.onTouched();
    this.selectionChange.emit(this.selectedItems);
    this.confirmSelection.emit(this.selectedItems);
    this.closePopover();
  }

  discardSelection() {
    this.tempSelectedItems = [...this.selectedItems];
    this.cancelSelection.emit();
    this.closePopover();
  }

  // Search and Filter
  filterOptions() {
    // Modalità server-side: NON emettiamo qui (chiamato anche da ngOnChanges → loop).
    // L'emissione avviene solo dall'handler dell'input (onSearchInput).
    if (this.serverSideSearch) {
      this.filteredOptions = [...this.options];
      return;
    }
    if (!this.searchTerm) {
      this.filteredOptions = [...this.options];
    } else {
      const term = this.searchTerm.toLowerCase();
      this.filteredOptions = this.options.filter(option =>
        this.getOptionLabel(option).toLowerCase().includes(term)
      );
    }
  }

  /**
   * Triggered SOLO dal cambio del searchbar (interazione utente).
   * In modalità server-side emette il termine al consumer; altrimenti rifiltra client-side.
   */
  onSearchInput() {
    if (this.serverSideSearch) {
      this.searchTermChange.emit(this.searchTerm || '');
    } else {
      this.filterOptions();
    }
  }

  // Utility Methods
  getOptionLabel(option: any): string {
    return option[this.displayField] || option.toString();
  }

  getOptionValue(option: any): any {
    return option[this.valueField] !== undefined ? option[this.valueField] : option;
  }

  getDisplayText(): string {
    if (this.selectedItems.length === 0) {
      return this.placeholder;
    } else if (this.selectedItems.length === 1) {
      return this.getOptionLabel(this.selectedItems[0]);
    } else {
      return `${this.selectedItems.length} items selected`;
    }
  }

  trackByFn = (index: number, option: any): any => {
    return this.getOptionValue(option);
  }

  // Public Methods for Template
  confirm() {
    this.applySelection();
  }

  cancel() {
    this.discardSelection();
  }
}
